import { useState } from 'react';

import { Button } from '../../button';

import {
  abridge,
  answeringPiece,
  BUDGET_QUESTION,
  BUDGET_REFINEMENT,
  budgetRuns,
  budgetSummary,
  budgetTransition,
  CHUNK_REFINEMENT,
  chunkQuestions,
  chunkSummary,
  chunkTransition,
  cutDocument,
  cutLabels,
  CUTS,
  DOCUMENTS,
  documentLabels,
  expectedText,
  initialBudget,
  initialChunk,
  initialKeyword,
  initialMiss,
  KEYWORD_QUESTIONS,
  KEYWORD_REFINEMENT,
  keywordFound,
  keywordHits,
  keywordSummary,
  keywordTransition,
  MISS_QUESTIONS,
  MISS_REFINEMENT,
  missOverlap,
  missPieces,
  missSummary,
  missTransition,
  pieceText,
  questionText,
  searchWords,
  SENDS,
  sendLabels,
  toCut,
  toDocument,
  toKeywordQuestion,
  toMissQuestion,
  topIds,
  toSend,
  verdict,
  type BudgetAction,
  type ChunkAction,
  type KeywordAction,
  type MissAction,
} from './know-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './know-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const wordsText = (words: readonly string[]) => words.join(' ') || '(no words)';
const REPLAY = 'This graphic replays real runs.';

export function KnowBudgetLab() {
  const [state, setState] = useState(initialBudget);
  const act = (action: BudgetAction) => setState((current) => budgetTransition(current, action));
  const run = budgetRuns[state.send];
  const summary = budgetSummary(state);
  return (
    <Lab
      title="What does sending everything cost, and what does it do to the answer?"
      eyebrow="Knowledge · the prompt budget"
      className="rel-lab know-lab"
    >
      <Predict>
        Does the answer survive when every piece is sent? Choose What to send, then press Ask how
        long each speaker may talk.
      </Predict>
      <div className="cb-controls">
        <label>
          What to send
          <ModelSelect
            aria-label="What to send"
            value={state.send}
            onValueChange={(value) => act({ type: 'send', value: toSend(value) })}
            options={SENDS.map((send) => ({ value: send, label: sendLabels[send] }))}
          />
        </label>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask how long each speaker may talk
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="The question">
          <pre aria-label="The question">{BUDGET_QUESTION}</pre>
        </Panel>
        <Panel label="What the prompt holds">
          <pre aria-label="What the prompt holds">{run.holds}</pre>
        </Panel>
      </div>
      <Panel label="The answer">
        <pre aria-label="The answer">{state.asked ? run.answer : '(not asked yet)'}</pre>
      </Panel>
      {state.asked ? (
        <Facts
          values={[
            ['Prompt tokens', run.promptTokens],
            ['Pieces sent', String(run.piecesSent)],
            ['Answer came from', state.send === 'five' ? '5.2' : 'the planted sentence in 12.2'],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{BUDGET_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function KnowChunkLab() {
  const [state, setState] = useState(initialChunk);
  const act = (action: ChunkAction) => setState((current) => chunkTransition(current, action));
  const pieces = cutDocument(state.document, state.cut);
  const asked = chunkQuestions[state.document];
  const answer = answeringPiece(pieces, asked.marker);
  const summary = chunkSummary(state);
  return (
    <Lab
      title="How big should a piece be?"
      eyebrow="Knowledge · cutting documents"
      className="rel-lab know-lab"
    >
      <Predict>Which cut keeps the answer whole? Choose Cut by, then choose Document.</Predict>
      <div className="cb-controls">
        <label>
          Cut by
          <ModelSelect
            aria-label="Cut by"
            value={state.cut}
            onValueChange={(value) => act({ type: 'cut', value: toCut(value) })}
            options={CUTS.map((cut) => ({ value: cut, label: cutLabels[cut] }))}
          />
        </label>
        <label>
          Document
          <ModelSelect
            aria-label="Document"
            value={state.document}
            onValueChange={(value) => act({ type: 'document', value: toDocument(value) })}
            options={DOCUMENTS.map((id) => ({ value: id, label: documentLabels[id] }))}
          />
        </label>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The question">
        <pre aria-label="The question">{questionText(asked.id)}</pre>
      </Panel>
      <Table
        label="Pieces"
        columns={['Piece', 'Words', 'Text']}
        template="minmax(0, 0.5fr) minmax(0, 0.5fr) minmax(0, 3fr)"
        rows={pieces.map((entry) => [entry.id, entry.words, abridge(entry.text)])}
      />
      <Facts
        values={[
          ['Pieces', String(pieces.length)],
          ['Answering piece', answer?.id ?? 'none'],
          ['Words in it', String(answer?.words ?? 0)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{CHUNK_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function KnowKeywordLab() {
  const [state, setState] = useState(initialKeyword);
  const act = (action: KeywordAction) => setState((current) => keywordTransition(current, action));
  const hits = keywordHits(state.question);
  const summary = keywordSummary(state);
  return (
    <Lab
      title="Which pieces do the words find?"
      eyebrow="Knowledge · search by words"
      className="rel-lab know-lab"
    >
      <Predict>
        Which pieces do the question's words find? Choose a question, then press Search by words.
      </Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toKeywordQuestion(value) })}
            options={KEYWORD_QUESTIONS.map((id) => ({ value: id, label: questionText(id) }))}
          />
        </label>
        <Button size="sm" disabled={state.searched} onClick={() => act({ type: 'search' })}>
          Search by words
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The question's words">
        <pre aria-label="The question's words">
          {wordsText(searchWords(questionText(state.question)))}
        </pre>
      </Panel>
      <Table
        label="Top three"
        columns={['Piece', 'Matching words']}
        template="minmax(0, 0.6fr) minmax(0, 2fr)"
        rows={state.searched ? hits.slice(0, 3).map((hit) => [hit.id, hit.matches.join(', ')]) : []}
      />
      {state.searched ? (
        <Facts
          values={[
            ['Expected', expectedText(state.question)],
            ['Top five', verdict(keywordFound(state.question))],
            ['Pieces with a match', String(hits.length)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{KEYWORD_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function KnowMissLab() {
  const [state, setState] = useState(initialMiss);
  const act = (action: MissAction) => setState((current) => missTransition(current, action));
  const expected = missPieces[state.question];
  const shared = missOverlap(state.question);
  const found = topIds(keywordHits(state.question), 5).includes(expected);
  const summary = missSummary(state);
  return (
    <Lab
      title="Why does the keyword search miss the venue decision?"
      eyebrow="Knowledge · words miss meaning"
      className="rel-lab know-lab"
    >
      <Predict>
        How many words does the question share with its piece? Choose a question, then press Show
        the piece's words.
      </Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toMissQuestion(value) })}
            options={MISS_QUESTIONS.map((id) => ({ value: id, label: questionText(id) }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.shown}
          onClick={() => act({ type: 'toggle' })}
        >
          Show the piece's words
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="cb-two">
        <Panel label="The question's words">
          <pre aria-label="The question's words">
            {wordsText(searchWords(questionText(state.question)))}
          </pre>
        </Panel>
        <Panel label={`Piece ${expected}'s words`}>
          <pre aria-label="The piece's words">
            {state.shown ? wordsText(searchWords(pieceText(expected))) : '(not shown yet)'}
          </pre>
        </Panel>
      </div>
      {state.shown ? (
        <Facts
          values={[
            ['Expected piece', expected],
            ['Words in common', String(shared.length)],
            ['Keyword verdict', verdict(found)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.shown ? <p className="rel-caption">{MISS_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
